import type { TableActions } from '@/components/table/types';
import { getAssetActionOptions } from '@/sections/asset/config/actionOptions/getAssetActionOptions';
import type { AssetActionHandlers } from '@/sections/asset/types';
import type {
  QueryActionHandlers,
  QueryEntity,
} from '@/sections/insights/queryBuilder/entities/types';
import type { Asset } from '@/types';

import { buildAssetColumns } from './columns';
import { getAssetFields } from './fields';
import { getAssetSimpleFilters } from './simpleFilters';

/**
 * Build row actions for an asset in QueryBuilder results
 * Adapter: wraps getAssetActionOptions (used by main AssetTable) for QueryBuilder context
 */
function buildAssetRowActions(
  item: unknown,
  handlers: QueryActionHandlers
): TableActions {
  const asset = item as Asset;

  const actionHandlers: AssetActionHandlers = handlers.assetHandlers ?? {
    handleScan: async () => {
      handlers.showToast('Scan initiated');
    },
    handleUpdateStatus: async () => {
      handlers.showToast('Status update initiated');
    },
    handleShare: () => {
      handlers.showToast('Share initiated');
    },
  };

  const allActions = getAssetActionOptions({
    assets: asset ? [asset] : [],
    onCommentClick: () => {
      if (handlers.onCommentClick) {
        handlers.onCommentClick(asset);
      } else {
        handlers.showToast('Comment action coming soon');
      }
    },
    onTagClick: () => {
      if (handlers.onTagClick) {
        handlers.onTagClick(asset);
      } else {
        handlers.showToast('Tag action coming soon');
      }
    },
    handlers: actionHandlers,
    selectUserAgent: {
      onAssetKeysChange: () => {
        // No-op in QueryBuilder context
      },
    },
    isPraetorianUser: handlers.isPraetorianUser,
  });

  return {
    menu: {
      items: allActions.map(option => ({
        ...option,
        value: option.value?.toString(),
      })),
    },
  };
}

/**
 * Asset QueryEntity - bundles columns, fields, and row actions
 */
export const assetEntity: QueryEntity<unknown> = {
  primaryLabel: { actual: 'Asset' },
  buildColumns:
    buildAssetColumns as () => import('@/components/table/types').Columns<unknown>,
  getFields: getAssetFields,
  getSimpleFilters: getAssetSimpleFilters,
  buildRowActions: buildAssetRowActions,
};
